
import { PermissionTypeEnum, TenantUserModel, LocationModel, RoleModel } from '@/libs/Api';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { Component, Prop, Ref, VModel, Vue, Watch } from 'vue-property-decorator';
import Autocomplete from '../components/Autocomplete.vue';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { rolesStore } from '@/libs/roles/+state/store';
import { Guid } from '../functions/guid';

@Component({})
export default class TenantUserSelect extends Vue {
    @VModel() innerValue!: string;
    @Prop() inputValue!: string | null | undefined;
    @Prop({ default: '' }) label!: string;
    @Prop() type!: string | Array<string>;
    @Prop() readonly!: boolean;
    @Prop() rules!: [any[]];
    @Prop({ default: false }) required!: boolean;
    @Prop({ default: false }) multiple!: boolean;
    @Prop({ default: false }) createParty!: boolean;
    @Prop({ default: false }) dense!: boolean;
    @Prop({ default: false }) noMarginTop!: boolean;
    @Prop({ default: true }) chips!: boolean;
    @Prop({ default: true }) hideDetails!: boolean;
    @Prop({ default: false }) showDepartment!: boolean;
    @Prop({ default: null }) categoryId!: string | null | Array<string>;
    @Prop() placeholder!: string;
    @Prop() exceptUsers!: string[];
    @Prop() permissions!: PermissionTypeEnum[];
    
    @Ref() autocomplete!: Autocomplete;

    protected tenantUserId: string | null = null;

    @Watch('inputValue')
    onInputValueChanged(newValue: string) {
        this.tenantUserId = newValue;
    }

    @Watch('innerValue')
    onInnerValueChanged(newValue: string) {
        this.tenantUserId = newValue;
    }

    mounted() {
        if (this.innerValue) {
            this.tenantUserId = this.innerValue;
        } else if (this.inputValue) {
            this.tenantUserId = this.inputValue;
        }
    }

    // Mapa rolí pro rychlý lookup podle ID
    get rolesMap(): Map<string, RoleModel> {
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        const map = new Map<string, RoleModel>();
        roles?.forEach((role) => {
            if (role.id) {
                map.set(role.id, role);
            }
        });
        return map;
    }

    // ✅ NOVÝ SYSTÉM: Filtruje uživatele podle kategorie-specifických oprávnění
    get items(): TenantUserModel[] {
        let items = [...(tenantUsersStore.useGetter(CrudGetter.Data) as TenantUserModel[])];
        
        // Filtrování vyjma určitých uživatelů
        if (this.exceptUsers) {
            items = items
                .where((x) => !this.exceptUsers.contains(x.id!) || x.id! == this.innerValue || x.id! == this.inputValue)
                .toArray();
        }
        
        // Filtrování podle oprávnění s ohledem na kategorii
        if (this.permissions && this.permissions?.length > 0) {
            items = items
                .where((x) => {
                    // Vždy zahrnout aktuálně vybraného uživatele
                    if (x.id! == this.innerValue || x.id! == this.inputValue) {
                        return true;
                    }

                    if (x.id == "7fd6617b-9863-4752-9812-f26ee9b6cfac" && this.innerValue == Guid.EmptyGuid()) {
                        debugger;
                    }

                    // Pokud je zadána kategorie, kontrolovat oprávnění v kontextu kategorie
                    if (this.categoryId != null) {
                        const categoryIds = typeof this.categoryId === 'string' 
                            ? [this.categoryId] 
                            : this.categoryId;
                        
                        // Pokud má přístup ke všem kategoriím, kontrolovat globální oprávnění
                        if (x.accessAllCategories) {
                            return x.userRoles?.any((n) => {
                                const role = this.rolesMap.get(n.roleId ?? '');
                                return role?.permissions?.any((j) => this.permissions.contains(j.permissionType!)) ?? false;
                            }) ?? false;
                        }
                        
                        // Kontrolovat oprávnění pro specifické kategorie
                        return x.categories?.any((cat) => {
                            if (!categoryIds.includes(cat.categoryId!)) {
                                return false;
                            }
                            
                            // Pokud se dědí globální role
                            if (cat.inheritedRoles) {
                                return x.userRoles?.any((n) => {
                                    const role = this.rolesMap.get(n.roleId ?? '');
                                    return role?.permissions?.any((j) => this.permissions.contains(j.permissionType!)) ?? false;
                                }) ?? false;
                            }
                            
                            // Jinak kontrolovat kategorie-specifické role
                            return cat.categoryUserRoles?.any((cur: any) => {
                                const role = this.rolesMap.get(cur.roleId ?? '');
                                return role?.permissions?.any((p: any) => this.permissions.contains(p.permissionType!)) ?? false;
                            }) ?? false;
                        }) ?? false;
                    }
                    
                    // Pokud není zadána kategorie, kontrolovat pouze globální oprávnění
                    return x.userRoles?.any((n) => {
                        const role = this.rolesMap.get(n.roleId ?? '');
                        return role?.permissions?.any((j) => this.permissions.contains(j.permissionType!)) ?? false;
                    }) ?? false;
                })
                .toArray();
        }
        
        /*
        OBSOLETE
        // Filtrování podle přístupu ke kategorii
        if (this.categoryId != null && typeof this.categoryId == 'string') {
            items = items.where((x) => x.categories?.any((c) => c.categoryId == this.categoryId) == true || x.accessAllCategories == true).toArray();
        } else if (this.categoryId && this.categoryId!.length > 0) {
            items = items.where((x) => x.categories?.any((c) => this.categoryId!.includes(c.categoryId!)) == true || x.accessAllCategories == true).toArray();
        }*/
        
        // Finální filtrování a řazení
        return items
            .where((x) => x.isActive == true || x.id! == this.innerValue || x.id! == this.inputValue)
            .toArray()
            .sort((a: TenantUserModel, b: TenantUserModel) => {
                if (a.user!.displayName! < b.user!.displayName!) {
                    return -1;
                }
                if (a.user!.displayName! > b.user!.displayName!) {
                    return 1;
                }
                return 0;
            });
    }

    tenantUserName(e: TenantUserModel): string {
        const baseName = e.user!.displayName!;

        const parts: string[] = [];
        if (this.showDepartment && e.department) {
            parts.push(e.department.name ?? '');
        }

        const locationNames = (e.locations ?? [])
            .map((x) => this.getLocationName(x.locationId))
            .filter((x) => (x ?? '').length > 0)
            .sort((a, b) => a.localeCompare(b));

        if (locationNames.length > 0) {
            parts.push(...locationNames);
        }

        if (parts.length > 0) {
            return `${baseName} (${parts.join(', ')})`;
        }
        return baseName;
    }

    getLocationName(locationId?: string | null): string {
        if (!locationId) return '';
        const all: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const found: LocationModel | undefined = (all as any).firstOrDefault
            ? (all as any).firstOrDefault((x: LocationModel) => x.id == locationId)
            : all.find((x) => x.id == locationId);
        return found?.name ?? '';
    }

    onChange(value: string | null) {
        this.$emit('input', value);
        this.$emit('change', value);
    }
}
